import { SelectField } from "@/components/design-system/SelectField";
import type { PortfolioSummary } from "@/portfolio/types";
import { t } from "@lingui/core/macro";

type PortfolioSelectorProps = {
  portfolios?: PortfolioSummary[];
  value: string;
  onChange: (id: string) => void;
  emptyLabel: string;
  disabled?: boolean;
  optionLabel?: (portfolio: PortfolioSummary) => string;
  isUnavailable?: (portfolio: PortfolioSummary) => boolean;
  selectedLabel?: string;
  label?: string | null;
  onFocus?: () => void;
};

type PortfolioOptionsProps = Pick<
  PortfolioSelectorProps,
  "portfolios" | "value" | "emptyLabel" | "optionLabel" | "isUnavailable" | "selectedLabel"
> &
  Required<Pick<PortfolioSelectorProps, "optionLabel" | "isUnavailable">>;

function createOptions({
  portfolios,
  value,
  emptyLabel,
  optionLabel,
  isUnavailable,
  selectedLabel,
}: PortfolioOptionsProps) {
  const selectedPortfolioIsMissing = value && !portfolios?.some((portfolio) => portfolio.id === value);
  const unavailableOption = selectedPortfolioIsMissing
    ? [{ label: selectedLabel || t`Unavailable portfolio`, value, disabled: true }]
    : [];
  const portfolioOptions = portfolios?.map((portfolio) => ({
    label: optionLabel(portfolio),
    value: portfolio.id,
    disabled: isUnavailable(portfolio),
  }));

  return [{ label: emptyLabel, value: "" }, ...unavailableOption, ...(portfolioOptions ?? [])];
}

export function PortfolioSelector({
  portfolios,
  value,
  onChange,
  emptyLabel,
  disabled,
  optionLabel = (portfolio) => portfolio.name,
  isUnavailable = () => false,
  selectedLabel,
  label = t`Portfolio`,
  onFocus,
}: PortfolioSelectorProps) {
  const options = createOptions({
    portfolios,
    value,
    emptyLabel,
    optionLabel,
    isUnavailable,
    selectedLabel,
  });

  return (
    <div onFocus={onFocus}>
      <SelectField
        {...(label ? { label } : {})}
        fullWidth
        disabled={disabled}
        value={value}
        options={options}
        onChange={(id) => {
          if (typeof id === "string") {
            onChange(id);
          }
        }}
      />
    </div>
  );
}
